import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { UserPlus, Send } from 'lucide-react';

export default function ManpowerRequestPage() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    project_id: '',
    position_id: '',
    request_qty: '',
    reason: '',
    urgency: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = authAxios();
        const [projRes, posRes] = await Promise.all([
          api.get('/projects'),
          api.get('/positions'),
        ]);
        // Filter projects for staff role
        if (user?.role === 'staff' && user?.assigned_projects?.length) {
          setProjects(projRes.data.filter(p => user.assigned_projects.includes(p.id)));
        } else {
          setProjects(projRes.data);
        }
        setPositions(posRes.data);
      } catch {
        toast.error('Failed to load form data');
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.position_id || !form.request_qty || !form.reason || !form.urgency) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const api = authAxios();
      await api.post('/manpower-requests', {
        ...form,
        request_qty: parseInt(form.request_qty),
      });
      toast.success('Manpower request submitted successfully');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="manpower-request-page" className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <UserPlus className="h-6 w-6" /> New Manpower Request
        </h1>
        <p className="text-sm text-slate-500 mt-1">Submit a request for additional staff</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold text-slate-800">Request Details</CardTitle>
          <CardDescription className="text-sm text-slate-500">Fill in the details below. All fields are required.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Project</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="request-project" className="h-10">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Position</Label>
                <Select value={form.position_id} onValueChange={(v) => setForm({ ...form, position_id: v })}>
                  <SelectTrigger data-testid="request-position" className="h-10">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.request_qty}
                  onChange={(e) => setForm({ ...form, request_qty: e.target.value })}
                  placeholder="Number of people"
                  data-testid="request-qty"
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger data-testid="request-urgency" className="h-10">
                    <SelectValue placeholder="Select urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Reason</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Describe why this manpower is needed..."
                data-testid="request-reason"
                rows={3}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="request-submit"
              className="w-full h-10 bg-[#0F172A] hover:bg-slate-800"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
              {!loading && <Send className="h-4 w-4 ml-1" />}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
